/**
 * K108-R Batch 2 — extension-evidence read model hook.
 *
 * Reads `training_session_extension_evidence` (RLS already permits technicians to
 * read their own rows, managers their shop) + `training_consumption_sessions`,
 * and returns a per-session engagement summary via the pure aggregator. NO DB
 * change, NO Edge change.
 *
 * Degrades gracefully: if the query fails (e.g. Safari evidence never persisted
 * due to the still-gated CORS 403, or no Companion) it returns
 * `evidenceUnavailable` summaries — it NEVER throws to the caller and NEVER
 * blocks a training launch.
 *
 * Batch 3 (completion modal) + Batch 4 (Learning History) consume this hook.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  aggregateSessionMonitoring,
  emptyMonitoringSummary,
  type MonitoringEvidenceRow,
  type MonitoringSessionInput,
  type TrainingSessionMonitoringSummary,
} from '@/lib/training/trainingSessionMonitoringSummary';

export type { TrainingSessionMonitoringSummary } from '@/lib/training/trainingSessionMonitoringSummary';

function isTrainingDebug(): boolean {
  try {
    return typeof window !== 'undefined' && localStorage.getItem('training_debug') === 'true';
  } catch {
    return false;
  }
}

export interface UseTrainingSessionMonitoringSummaryOptions {
  sessionIds: string[];
  enabled?: boolean;
}

/**
 * Returns a `Map<sessionId, summary>` for the given sessions. Always contains an
 * entry for every requested id (empty summary when no evidence / on error).
 */
export function useTrainingSessionMonitoringSummary({
  sessionIds,
  enabled = true,
}: UseTrainingSessionMonitoringSummaryOptions) {
  const ids = Array.from(new Set(sessionIds.filter(Boolean)));
  const query = useQuery<Map<string, TrainingSessionMonitoringSummary>>({
    queryKey: ['training-session-monitoring-summary', ids],
    enabled: enabled && ids.length > 0,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const out = new Map<string, TrainingSessionMonitoringSummary>();
      // Pre-seed empties so a partial/failed read still yields a full map.
      for (const id of ids) out.set(id, emptyMonitoringSummary(id));
      try {
        const [{ data: sessions }, { data: rows }] = await Promise.all([
          supabase
            .from('training_consumption_sessions')
            .select('id, expected_duration_minutes')
            .in('id', ids),
          supabase
            .from('training_session_extension_evidence')
            .select('session_id, evidence_type, active_seconds, visible_seconds, played_seconds, submitted_at, metadata')
            .in('session_id', ids),
        ]);

        const bySession = new Map<string, MonitoringEvidenceRow[]>();
        for (const r of (rows ?? []) as MonitoringEvidenceRow[]) {
          const list = bySession.get(r.session_id);
          if (list) list.push(r);
          else bySession.set(r.session_id, [r]);
        }
        const sessionById = new Map<string, MonitoringSessionInput>();
        for (const s of (sessions ?? []) as MonitoringSessionInput[]) sessionById.set(s.id, s);

        for (const id of ids) {
          const session = sessionById.get(id) ?? { id, expected_duration_minutes: null };
          out.set(id, aggregateSessionMonitoring(session, bySession.get(id) ?? []));
        }
      } catch {
        // Safari-403 / RLS / network — return the pre-seeded empty summaries.
      }
      return out;
    },
  });

  // Dev-only, NON-SECRET visibility for live testing (localStorage training_debug).
  useEffect(() => {
    if (!query.data || !isTrainingDebug()) return;
     
    console.debug('[TrainingMonitoringSummary]', Array.from(query.data.values()));
  }, [query.data]);

  return query;
}
